import type { FragmentType } from '@packages/gql/generated/shopping';
import type { GetGlycerinDisplayProductPayloadFragmentFragmentDoc } from '@packages/gql/generated/shopping/GetGlycerinDisplayProductPayloadFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { getGlycerinArticleId } from '@packages/modules/src/Tracking/helper/getGlycerinArticleId/getGlycerinArticleId';
import { getGlycerinAvailability } from '@packages/modules/src/Tracking/helper/getGlycerinAvailability/getGlycerinAvailability';
import { getGlycerinMkz } from '@packages/modules/src/Tracking/helper/getGlycerinMkz';
import { getGlycerinOrigin } from '@packages/modules/src/Tracking/helper/getGlycerinOrigin/getGlycerinOrigin';
import { getGlycerinServices } from '@packages/modules/src/Tracking/helper/getGlycerinServices';
import { type GlycerinDisplayProductPayload } from '@packages/tracking/src/types/payloads';
import type { getCategoryData } from '../getCategoryData';
import { getGlycerinCategoryId } from '../getGlycerinCategoryId';
import { getGlycerinPrice } from '../getGlycerinPrice';
import { getGlycerinRatingStatus } from '../getGlycerinRatingStatus';
import { getSizeAndVariantFromDimensions } from '../getSizeAndVariantFromDimensions';
import { getStockValuesFromDimensions } from '../getStockValuesFromDimensions';

/* GraphQL */ `
  fragment GetGlycerinDisplayProductPayloadFragment on Product {
    akl
    itemOptionCommKey
    name
    orderNumber
    promotion
    sku
    availability {
      message
      state
    }
    price {
      value
      valueOld
      currency
    }
    seoRatingGetGlycerinDisplayProductPayload: seoRating(locale: $locale) {
      ...GetGlycerinRatingStatusFragment
    }
    aplusGetGlycerinDisplayProductPayload: aplus(language: $lang) @skip(if: $skipAplus) {
      ...GetGlycerinServicesFragment
    }
    ...GetGlycerinArticleIdFragment
    ...GetGlycerinMkz
    ...GetSizeAndVariantFromDimensionsFragment
    ...GetStockValuesFromDimensionsFragment
  }
`;

export type GetGlycerinDisplayProductPayloadProps = {
  categoryPath: ReturnType<typeof getCategoryData>['categoryPath'];
  data: FragmentType<typeof GetGlycerinDisplayProductPayloadFragmentFragmentDoc>;
  categoryIdSearchParam: string | null;
  refSearchParam: string | null;
};

export const getGlycerinDisplayProductPayload = ({
  categoryPath,
  data,
  categoryIdSearchParam,
  refSearchParam,
}: GetGlycerinDisplayProductPayloadProps): GlycerinDisplayProductPayload => {
  const unmaskedData = unmask<typeof GetGlycerinDisplayProductPayloadFragmentFragmentDoc>(data);
  const {
    akl,
    availability,
    itemOptionCommKey,
    name,
    orderNumber,
    price,
    promotion,
    sku,
    seoRatingGetGlycerinDisplayProductPayload,
    aplusGetGlycerinDisplayProductPayload,
  } = unmaskedData;

  const { inStock, outOfStock } = getStockValuesFromDimensions(unmaskedData);
  const { size, variant } = getSizeAndVariantFromDimensions(unmaskedData);

  return {
    productId: akl,
    skuId: sku,
    variantsInStock: inStock,
    variantsOutOfStock: outOfStock,
    ...getGlycerinArticleId(unmaskedData),
    ...getGlycerinAvailability(availability),
    ...getGlycerinCategoryId(categoryPath, categoryIdSearchParam),
    ...getGlycerinOrigin(refSearchParam),
    ...getGlycerinPrice(price),
    ...getGlycerinRatingStatus(seoRatingGetGlycerinDisplayProductPayload),
    ...getGlycerinServices({
      aplus: aplusGetGlycerinDisplayProductPayload,
      currency: price.currency,
    }),
    custom: {
      gtmData: {
        products: [
          {
            ...getGlycerinMkz(unmaskedData),
            categoryShops: categoryPath,
            itemOptionComKey: itemOptionCommKey || undefined,
            productName: name,
            orderNumber,
            promotion: promotion ?? '',
            size,
            variant,
          },
        ],
      },
    },
  };
};
